import { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { apiFetch } from './apiBase';

export default function MfaSettings() {
  const [status, setStatus] = useState({ loading: true, available: false, enabled: false });
  const [setup, setSetup] = useState(null); // { secret, otpauthUrl, qrDataUrl }
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = () =>
    apiFetch('/auth/mfa/status')
      .then((d) => setStatus({ loading: false, available: !!d.available, enabled: !!d.enabled }))
      .catch(() => setStatus({ loading: false, available: false, enabled: false }));

  useEffect(() => {
    refresh();
  }, []);

  const beginSetup = async () => {
    setError('');
    setBusy(true);
    try {
      setSetup(await apiFetch('/auth/mfa/setup', { method: 'POST' }));
      setBackupCodes(null);
      setCode('');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const enable = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const d = await apiFetch('/auth/mfa/enable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });
      setBackupCodes(d.backupCodes || []);
      setSetup(null);
      setCode('');
      await refresh();
    } catch (e2) {
      setError(e2.message);
    } finally {
      setBusy(false);
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await apiFetch('/auth/mfa/disable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });
      setCode('');
      await refresh();
    } catch (e2) {
      setError(e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-panel max-w-2xl p-5">
      <div className="mb-4 flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-cyan-500" />
        <h3 className="font-semibold text-slate-100">Two-factor authentication (MFA)</h3>
      </div>

      {status.loading && <p className="text-sm text-slate-400">Checking status…</p>}

      {!status.loading && !status.available && (
        <p className="text-sm text-slate-400">
          Two-factor authentication is available for registered accounts. Sign in with a real
          account (not the local dev bypass) to enable it.
        </p>
      )}

      {!status.loading && status.available && (
        <>
          <p className="mb-4 text-sm text-slate-400">
            Status:{' '}
            <span className={status.enabled ? 'font-semibold text-emerald-400' : 'text-slate-300'}>
              {status.enabled ? 'Enabled' : 'Not enabled'}
            </span>
          </p>

          {error && <p className="alert-error mb-3 text-sm">{error}</p>}

          {backupCodes && (
            <div className="mb-4 rounded-lg border border-amber-600/40 bg-amber-950/20 p-4">
              <p className="text-sm font-medium text-amber-300">
                Save these one-time backup codes now — they won’t be shown again.
              </p>
              <div className="mt-2 grid grid-cols-2 gap-1 font-mono text-sm text-slate-200">
                {backupCodes.map((c) => (
                  <span key={c}>{c}</span>
                ))}
              </div>
            </div>
          )}

          {!status.enabled && !setup && (
            <button type="button" className="btn-primary px-4 py-2" disabled={busy} onClick={beginSetup}>
              Set up two-factor
            </button>
          )}

          {!status.enabled && setup && (
            <form onSubmit={enable} className="space-y-3">
              <p className="text-sm text-slate-400">
                Scan this QR code with an authenticator app (Google Authenticator, Authy, 1Password),
                then enter the 6-digit code to confirm.
              </p>
              {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
              <img
                src={setup.qrDataUrl}
                alt="MFA QR code"
                className="h-44 w-44 rounded bg-white p-2"
              />
              <p className="break-all text-xs text-slate-500">
                Or enter this secret manually: <span className="font-mono">{setup.secret}</span>
              </p>
              <input
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="input-field max-w-xs"
                placeholder="6-digit code"
                autoComplete="one-time-code"
              />
              <div>
                <button type="submit" className="btn-primary px-4 py-2" disabled={busy}>
                  Confirm &amp; enable
                </button>
              </div>
            </form>
          )}

          {status.enabled && (
            <form onSubmit={disable} className="space-y-3">
              <p className="text-sm text-slate-400">
                Enter a current authentication code (or a backup code) to turn off two-factor.
              </p>
              <input
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="input-field max-w-xs"
                placeholder="6-digit or backup code"
                autoComplete="one-time-code"
              />
              <div>
                <button
                  type="submit"
                  className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-500 disabled:opacity-60"
                  disabled={busy}
                >
                  Disable two-factor
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
}
